import React, { useState, useEffect } from 'react';
import { Activity, User, Plus, FileText, ArrowLeft, Search } from 'lucide-react';
import { api } from '../lib/api';
import Modal from '../components/Modal';

export default function TherapyManagement({ type, user, notify, navigate }) {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [modal, setModal] = useState(null);

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const data = await api('/patients');
                // Filter patients who have at least one session of the specified therapy type
                // In a real app, we'd do this server-side with a specific endpoint
                setPatients(data.patients);
            } catch (e) { notify(e.message); }
            finally { setLoading(false); }
        }
        load();
    }, [type, notify]);

    const filteredPatients = patients.filter(p =>
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(filter.toLowerCase()) ||
        p.mrn?.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="content-card">
            <div className="content-heading">
                <div>
                    <div className="eyebrow">{type === 'PT' ? 'Physical Therapy' : 'Occupational Therapy'} Management</div>
                    <h3>{type === 'PT' ? 'Physical Therapy' : 'Occupational Therapy'} Registry</h3>
                    <p>Coordinate evaluations and track progress across the facility.</p>
                </div>
                <div className="heading-actions">
                    <button className="button primary" onClick={() => setModal({ type: 'new-session' })}><Plus size={15}/> New Session</button>
                </div>
            </div>

            <div className="filter-bar" style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
                <div className="filter-inline" style={{ flex: 1 }}>
                    <Search size={16}/>
                    <input
                        placeholder={`Search patients for ${type}...`}
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="empty-copy">Loading therapy records...</div>
            ) : (
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Patient</th>
                                <th>MRN</th>
                                <th>Last Evaluation</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPatients.map(p => (
                                <tr key={p.id}>
                                    <td><strong onClick={() => navigate(`/patient/${p.id}`)} style={{ cursor: 'pointer', color: 'var(--primary-color)' }}>{p.first_name} {p.last_name}</strong></td>
                                    <td>{p.mrn || '—'}</td>
                                    <td>—</td>
                                    <td><span className="badge neutral">Active</span></td>
                                    <td>
                                        <button className="text-button" onClick={() => navigate(`/patient/${p.id}`)}>Open Chart</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!filteredPatients.length && <p className="empty-copy">No patients found matching the filter.</p>}
                </div>
            )}

            {modal?.type === 'new-session' && (
                <NewTherapyModal
                    type={type}
                    patients={patients}
                    user={user}
                    close={() => setModal(null)}
                    notify={notify}
                    navigate={navigate}
                />
            )}
        </div>
    );
}

function NewTherapyModal({ type, patients, user, close, notify, navigate }) {
    const [form, setForm] = useState({ patient_id: '', therapist: '', evaluation_date: new Date().toISOString().slice(0, 10), diagnosis_medical_necessity: '', plan_of_care: '' });
    const [busy, setBusy] = useState(false);

    async function save(e) {
        e.preventDefault(); setBusy(true);
        try {
            const body = { ...form, therapy_type: type };
            await api(`/patients/${form.patient_id}/therapy`, { method: 'POST', body });
            notify(`${type} session created successfully.`);
            close();
        } catch (e2) { notify(e2.message); }
        finally { setBusy(false); }
    }

    return (
        <Modal title={`New ${type === 'PT' ? 'Physical' : 'Occupational'} Therapy Session`} onClose={close}>
            <form className="admin-form" onSubmit={save}>
                <label>Patient
                    <select required value={form.patient_id} onChange={e => setForm({ ...form, patient_id: e.target.value })}>
                        <option value="">Select patient...</option>
                        {patients.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.mrn})</option>)}
                    </select>
                </label>
                <label>Therapist<input required value={form.therapist} onChange={e => setForm({ ...form, therapist: e.target.value })}/></label>
                <label>Evaluation Date<input type="date" required value={form.evaluation_date} onChange={e => setForm({ ...form, evaluation_date: e.target.value })}/></label>
                <label>Medical Necessity / Diagnosis<textarea rows={3} value={form.diagnosis_medical_necessity} onChange={e => setForm({ ...form, diagnosis_medical_necessity: e.target.value })}/></label>
                <label>Plan of Care<textarea rows={3} value={form.plan_of_care} onChange={e => setForm({ ...form, plan_of_care: e.target.value })}/></label>
                <button className="button primary" disabled={busy}>{busy ? 'Saving...' : 'Create Session'}</button>
            </form>
        </Modal>
    );
}
